'use client';

import { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface StepCard {
  step: number;
  title: string;
  tagline: string;
  icon: string;
  frontDetails: string[];
  backTitle: string;
  backItems: { label: string; value: string }[];
  accentColor: string;
  borderColor: string;
}

const cards: StepCard[] = [
  {
    step: 1,
    title: 'Find Your Gaps',
    tagline: 'Know where you stand before you start.',
    icon: 'MagnifyingGlassIcon',
    frontDetails: ['12-question adaptive diagnostic', 'Covers all AP Bio units', 'Takes under 8 minutes'],
    backTitle: 'What you\'ll discover',
    backItems: [
      { label: 'Topics assessed', value: '8 core units' },
      { label: 'Difficulty levels', value: '3 tiers' },
      { label: 'Time to complete', value: '~7 minutes' },
      { label: 'Output', value: 'Personal topic map' },
    ],
    accentColor: 'text-green',
    borderColor: 'border-green',
  },
  {
    step: 2,
    title: 'Build Your Map',
    tagline: 'See the whole picture, then zoom in.',
    icon: 'MapIcon',
    frontDetails: ['Visual concept web per unit', 'Highlights your green/amber/mauve zones', 'Links to targeted drill sets'],
    backTitle: 'Inside your map',
    backItems: [
      { label: 'Concept nodes', value: '200+ linked' },
      { label: 'Connections shown', value: 'Cross-unit links' },
      { label: 'Update frequency', value: 'After each session' },
      { label: 'Export', value: 'PDF or share link' },
    ],
    accentColor: 'text-mauve',
    borderColor: 'border-mauve',
  },
  {
    step: 3,
    title: 'Drill the Details',
    tagline: 'Targeted practice, not random review.',
    icon: 'BoltIcon',
    frontDetails: ['1,200+ practice questions', 'Sorted by your gap zones', 'Spaced repetition built in'],
    backTitle: 'The drill bank',
    backItems: [
      { label: 'Total questions', value: '1,247' },
      { label: 'Flashcard sets', value: '84 decks' },
      { label: 'Mnemonic packs', value: '32 topics' },
      { label: 'Avg session', value: '18 minutes' },
    ],
    accentColor: 'text-amber',
    borderColor: 'border-amber',
  },
  {
    step: 4,
    title: 'Simulate the Exam',
    tagline: 'Full-length mock under real conditions.',
    icon: 'ClipboardDocumentCheckIcon',
    frontDetails: ['AP Bio & MCAT format mocks', 'Timed, scored, explained', 'Predicted score range included'],
    backTitle: 'Mock exam details',
    backItems: [
      { label: 'Full-length mocks', value: '6 exams' },
      { label: 'Questions each', value: '60 AP / 59 MCAT' },
      { label: 'Detailed explanations', value: 'Every question' },
      { label: 'Predicted score', value: 'Included' },
    ],
    accentColor: 'text-green',
    borderColor: 'border-green',
  },
];

export default function StudyPathCards() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="study-path" ref={sectionRef} className="py-28 px-6 md:px-12 bg-cream">
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-16 reveal">
          <span className="text-xs font-semibold tracking-widest uppercase text-mauve block mb-4">
            The Study Path
          </span>
          <h2 className="font-fraunces text-5xl md:text-6xl font-light text-ink leading-tight mb-4">
            Four stages.<br />
            <span className="italic text-green">One clear path.</span>
          </h2>
          <p className="text-ink-muted text-lg max-w-xl mx-auto font-light">
            Each card flips to reveal exactly what's inside — no mystery, no filler.
          </p>
        </div>

        {/* Card grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map((card, i) => (
            <div
              key={card.step}
              className={`reveal reveal-delay-${i + 1} flip-card h-80`}
            >
              <div className="flip-card-inner w-full h-full">
                {/* Front */}
                <div className={`flip-card-front bg-cream-dark border-2 ${card.borderColor} p-6 flex flex-col`}>
                  <div className="flex items-start justify-between mb-4">
                    <span className={`text-4xl font-fraunces font-light ${card.accentColor} leading-none`}>
                      {card.step}
                    </span>
                    <div className={`w-10 h-10 rounded-xl bg-cream flex items-center justify-center ${card.accentColor}`}>
                      <Icon name={card.icon as any} size={20} />
                    </div>
                  </div>
                  <h3 className="font-fraunces text-xl font-semibold text-ink mb-2">{card.title}</h3>
                  <p className="text-ink-muted text-sm font-light mb-4 leading-relaxed">{card.tagline}</p>
                  <ul className="mt-auto space-y-1.5">
                    {card.frontDetails.map((detail, j) => (
                      <li key={j} className="flex items-center gap-2 text-xs text-ink-light">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 bg-current ${card.accentColor}`} />
                        {detail}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[10px] text-ink-muted mt-4 text-center tracking-wide">Hover to see inside →</p>
                </div>

                {/* Back */}
                <div className={`flip-card-back bg-ink p-6 flex flex-col`}>
                  <h4 className="font-fraunces text-lg font-medium text-cream mb-5">{card.backTitle}</h4>
                  <div className="space-y-3 flex-1">
                    {card.backItems.map((item, j) => (
                      <div key={j} className="flex justify-between items-center border-b border-white/10 pb-2">
                        <span className="text-xs text-cream/60 font-light">{item.label}</span>
                        <span className="text-sm font-semibold text-amber">{item.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-white/10">
                    <span className="text-xs text-cream/40 font-light">Step {card.step} of 4</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}